import 'reflect-metadata';
import type {
  UtIdProvider,
  CurrentLang,
  GaIdProvider,
  GetLogger,
  IFeatureFlagsStore
} from '@unitedtraders/utex-site-common';
import { globalHistory, History } from '@reach/router';
import type { CookiesStatic } from 'js-cookie';
import type { ApiClient } from '@unitedtraders/dizraptor-gateway-api-sdk';
import type { IZendeskChatApi } from '@type/Zendesk';
import type { AnyUserCard } from '@models/UserCard';
import type { PodsightsApi } from '@services/podsightsProvider';
import type { SessionService } from '@services/SessionService';
import type { IsUserAuthorized } from '@services/isUserAuthorized';
import type { NavigationService } from '@services/NavigationService';
import type { IdeasDict } from '@stores/IdeasStore';
import { AuthModalService } from '@services/AuthModalService';
import { VisitCollector } from '@services/VisitCollector';
import { AuthStore } from '@stores/AuthStore';
import type { UserCardsDict } from '@stores/UserCardsStore';
import type { EventBus } from '@services/EventBus';
import type { AppConfig } from '@type/AppConfig';
import type { StepsModalService } from '@components/CardModal/components/Steps/StepsModalService';
import { iocNames } from '../iocNames';
import { createIOC as commonCreateIOC } from './common';

declare global {
  interface Window {
    _ioc: unknown;
    _iocNames: unknown;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Clazz<T> = new (...args: any[]) => T;

export const createIOC = () => {
  const ioc = commonCreateIOC();

  ioc.bind(iocNames.window).toConstantValue(window);
  ioc.bind(iocNames.document).toConstantValue(document);
  ioc.bind(iocNames.navigator).toConstantValue(navigator);
  ioc.bind(iocNames.history).toConstantValue(globalHistory);
  ioc.bind(iocNames.localStorage).toConstantValue(localStorage);
  ioc.bind(iocNames.crypto).toConstantValue(crypto);
  // eslint-disable-next-line no-restricted-globals
  ioc.bind(iocNames.location).toConstantValue(location);

  ioc
    .bind(iocNames.visitCollector)
    .toDynamicValue(
      ({ container }) =>
        new VisitCollector(
          container.get<Window>(iocNames.window),
          container.get<Document>(iocNames.document),
          container.get<AppConfig>(iocNames.appConfig).visitCollectorUrl,
          () => container.get<GaIdProvider>(iocNames.gaIdProvider).getId(),
          () => container.get<UtIdProvider>(iocNames.utIdProvider).getId(),
          container.get<GetLogger>(iocNames.getLogger)('VisitCollector'),
          container.get<Promise<PodsightsApi>>(iocNames.podsightsApi)
        )
    )
    .inSingletonScope();

  ioc
    .bind(iocNames.authModalService)
    .toDynamicValue(({ container }) => {
      const location = container.get<Location>(iocNames.location);
      const history = container.get<History>(iocNames.history);
      const config = container.get<AppConfig>(iocNames.appConfig);
      const ss = container.get<SessionService>(iocNames.sessionService);
      const currentLang = container.get<CurrentLang>(iocNames.currentLang);
      const gaIdProvider = container.get<GaIdProvider>(iocNames.gaIdProvider);
      const utIdProvider = container.get<UtIdProvider>(iocNames.utIdProvider);
      const zendeskApi = container.get<Promise<IZendeskChatApi>>(iocNames.zendeskWidget);
      const podsightApi = container.get<Promise<PodsightsApi>>(iocNames.podsightsApi);
      const isUserAuthorized = container.get<IsUserAuthorized>(iocNames.isUserAuthorized);
      const visitCollector = ioc.get<VisitCollector>(iocNames.visitCollector);

      return new AuthModalService(
        location,
        history,
        config,
        ss,
        currentLang,
        gaIdProvider,
        utIdProvider,
        zendeskApi,
        visitCollector,
        podsightApi,
        isUserAuthorized
      );
    })
    .inSingletonScope();

  ioc.bind(iocNames.authStore).to(AuthStore).inSingletonScope();

  ioc.bind(iocNames.stepsModalServiceFactory).toDynamicValue(() => {
    return (Constructor: typeof StepsModalService) => {
      return (card: AnyUserCard, ideaId: number) => {
        const nav = ioc.get<NavigationService>(iocNames.navigationService);
        const api = ioc.get<ApiClient>(iocNames.dizraptorApi);
        const ideasDict = ioc.get<IdeasDict>(iocNames.ideasStore);
        const cardsDict = ioc.get<UserCardsDict>(iocNames.userCardsStore);

        const isUserAuthorized = ioc.get<IsUserAuthorized>(iocNames.isUserAuthorized);
        const crypto = ioc.get<Crypto>(iocNames.crypto);
        const cookies = ioc.get<CookiesStatic>(iocNames.cookies);
        const featureFlags = ioc.get<IFeatureFlagsStore>(iocNames.featureFlagsStore);
        const eventBus = ioc.get<EventBus>(iocNames.eventBus);

        return new Constructor(
          card,
          ideaId,
          nav,
          api,
          ideasDict,
          cardsDict,
          isUserAuthorized,
          crypto,
          cookies,
          featureFlags,
          eventBus
        );
      };
    };
  });

  return ioc;
};
